import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import {
  AiSkillsOverviewQuery,
  AiSkillsOverviewQueryVariables,
  SkillVisibilityEnum,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import GraphqlError from "@components/error/graphql-error";
import Heading from "@components/heading/heading";
import Loading from "@components/loading/loading";
import Placeholder from "@components/placeholder/placeholder";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";

import aiSkillsOverviewQuery from "../../graphql/ai-skills-overview-query";
import archiveSkillMutation from "../../graphql/archive-skill-mutation";
import SkillEditModal from "../skills/skill-edit-modal";
import SkillCard, { SkillCardData } from "./skill-card";
import useAdminOrganization from "./use-admin-organization";

const UserIcon = getTopicflowIcon("user");
const TeamIcon = getTopicflowIcon("team");

const optionLabel = (
  Icon: React.ComponentType<{ className?: string }>,
  text: string,
) => (
  <span className="flex items-center gap-2">
    <Icon className="h-4 w-4 text-gray-500" />
    {text}
  </span>
);

const AiSkillsPage = () => {
  const label = useLabel();
  const { orgId, canManageOrgAi } = useAdminOrganization();

  const refetchQueries = [
    {
      query: aiSkillsOverviewQuery,
      variables: { organizationId: orgId ?? null },
    },
  ];

  const { data, loading, error } = useQuery<
    AiSkillsOverviewQuery,
    AiSkillsOverviewQueryVariables
  >(aiSkillsOverviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: { organizationId: orgId ?? null },
  });

  const [archive, { loading: archiving }] = useMutation(archiveSkillMutation, {
    refetchQueries,
  });

  const [editing, setEditing] = useState<{
    open: boolean;
    skill?: SkillCardData;
    organizationId?: number;
    readOnly?: boolean;
  }>({ open: false });

  const skills: SkillCardData[] = (data?.aiSkillsOverview ?? []).map(
    (skill) => ({
      id: skill.__typename === "SkillNode" ? skill.id : null,
      name: skill.name,
      slug: skill.slug,
      description: skill.description,
      body: skill.body,
      canEdit: skill.canEdit,
      canArchive: skill.canArchive,
      visibility: skill.visibility,
      overridesSource: skill.overridesSource,
      isScheduled: skill.isScheduled,
    }),
  );

  const handleArchive = async (skill: SkillCardData) => {
    if (!skill.id) return;
    if (!window.confirm(`Are you sure you want to archive "${skill.name}"?`)) {
      return;
    }
    try {
      await archive({ variables: { id: skill.id } });
      successNotificationVar({ title: "Skill archived" });
    } catch (e) {
      errorNotificationVar({
        title: "Could not archive skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const createOptions: DropdownOptionType[] = [
    {
      label: optionLabel(UserIcon, "Personal Skill"),
      onClick: () => setEditing({ open: true, organizationId: undefined }),
    },
    ...(canManageOrgAi && orgId
      ? [
          {
            label: optionLabel(TeamIcon, "Workspace Skill"),
            onClick: () => setEditing({ open: true, organizationId: orgId }),
          },
        ]
      : []),
  ];

  return (
    <div aria-label="AI skills page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <Heading small title="Skills" />
        <Dropdown options={createOptions} align="end">
          <Button theme="primary" aria-label="Create skill">
            Create Skill
            <TopicflowIcon
              type="chevronDown"
              className="h-4 w-4 text-white shrink-0 pointer-events-none"
            />
          </Button>
        </Dropdown>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Reusable skills you can invoke with{" "}
        <code className="bg-gray-100 px-1 rounded">/skill-name</code> in
        Topicflow AI. This shows your personal skills, skills shared by your{" "}
        {label("organization")}, and built-in platform skills.
      </p>

      <SkillEditModal
        isOpen={editing.open}
        onClose={() => setEditing({ open: false })}
        organizationId={editing.organizationId}
        refetchQueries={refetchQueries}
        readOnly={editing.readOnly}
        initial={
          editing.skill
            ? {
                id: editing.skill.id ?? undefined,
                name: editing.skill.name,
                description: editing.skill.description,
                body: editing.skill.body,
              }
            : undefined
        }
      />

      {error && skills.length === 0 ? (
        <GraphqlError
          error={error}
          whatDidNotWork="Your skills could not be loaded."
        />
      ) : loading && skills.length === 0 ? (
        <div className="flex justify-center mt-8">
          <Loading mini size="5" />
        </div>
      ) : skills.length === 0 ? (
        <Placeholder>
          You don&apos;t have any skills yet. Click &quot;Create Skill&quot; to
          get started.
        </Placeholder>
      ) : (
        <div className="flex flex-col gap-2">
          {skills.map((skill) => {
            const openSkill = () =>
              setEditing({
                open: true,
                skill,
                organizationId:
                  skill.visibility === SkillVisibilityEnum.Workspace
                    ? (orgId ?? undefined)
                    : undefined,
                readOnly: !skill.canEdit,
              });
            const actions: DropdownOptionType[] = [
              {
                label: "Test in Topicflow AI",
                onClick: () =>
                  window.open(
                    `/ask-ai?p=${encodeURIComponent(`/${skill.slug}`)}`,
                    "_blank",
                  ),
              },
            ];
            if (skill.canEdit) {
              actions.push({
                label: "Edit",
                onClick: openSkill,
              });
            }
            if (skill.canArchive) {
              actions.push({ type: "divider" });
              actions.push({
                label: "Archive",
                textColor: "text-red-600",
                disabled: archiving,
                onClick: () => handleArchive(skill),
              });
            }
            return (
              <SkillCard
                key={skill.slug}
                skill={skill}
                actions={actions}
                onClick={openSkill}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AiSkillsPage;
